// Modale bloquante déclenchée au login quand user.force_password_change === true.
// - Pas de fermeture au clic-hors
// - Pas de fermeture via Escape
// - Pas de bouton "Passer"
// Utilise PUT /api/auth/change-password existant (flip du flag côté backend).
// Au succès, onSuccess() est appelée pour mettre à jour le user en mémoire.

import React, { useState } from 'react';
import axios from 'axios';
import { Lock, ShieldCheck } from 'lucide-react';
import { toast } from '../ui/Toast';
import { getPasswordRules } from '../../utils/passwordValidator';
import './ForcePasswordChangeModal.css';

export default function ForcePasswordChangeModal({ open, onSuccess }) {
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const rules = getPasswordRules(newPwd);
  const allRulesOk = rules.every(r => r.ok);
  const match = newPwd.length > 0 && newPwd === confirmPwd;
  const canSubmit = currentPwd.length > 0 && allRulesOk && match && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.put('/api/auth/change-password', {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success('Mot de passe mis à jour');
      // Purge immédiate du state — évite toute trace dans le DOM
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
      onSuccess?.();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Erreur lors du changement de mot de passe';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fpc-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fpc-modal-title"
    >
      <form className="fpc-modal__card" onSubmit={handleSubmit}>
        <div className="fpc-modal__icon" aria-hidden="true">
          <ShieldCheck size={22} strokeWidth={1.8} />
        </div>
        <h2 id="fpc-modal-title" className="fpc-modal__title">
          Changement de mot de passe requis
        </h2>
        <p className="fpc-modal__subtitle">
          Votre mot de passe actuel est temporaire. Définissez-en un nouveau
          pour continuer vers votre espace ITRIIA.
        </p>

        <div className="fpc-modal__field">
          <label className="fpc-modal__label" htmlFor="fpc-current">
            Mot de passe actuel
          </label>
          <input
            id="fpc-current"
            className="fpc-modal__input"
            type="password"
            autoComplete="current-password"
            value={currentPwd}
            onChange={e => setCurrentPwd(e.target.value)}
            autoFocus
            required
          />
        </div>

        <div className="fpc-modal__field">
          <label className="fpc-modal__label" htmlFor="fpc-new">
            Nouveau mot de passe
          </label>
          <input
            id="fpc-new"
            className="fpc-modal__input"
            type="password"
            autoComplete="new-password"
            value={newPwd}
            onChange={e => setNewPwd(e.target.value)}
            required
          />
          <ul className="fpc-modal__rules" aria-live="polite">
            {rules.map(r => (
              <li
                key={r.key}
                className={`fpc-modal__rule ${r.ok ? 'fpc-modal__rule--ok' : ''}`}
              >
                <span className="fpc-modal__rule-dot" aria-hidden="true" />
                {r.label}
              </li>
            ))}
          </ul>
        </div>

        <div className="fpc-modal__field">
          <label className="fpc-modal__label" htmlFor="fpc-confirm">
            Confirmer le nouveau mot de passe
          </label>
          <input
            id="fpc-confirm"
            className="fpc-modal__input"
            type="password"
            autoComplete="new-password"
            value={confirmPwd}
            onChange={e => setConfirmPwd(e.target.value)}
            required
          />
          {confirmPwd.length > 0 && !match && (
            <div className="fpc-modal__error">Les deux mots de passe ne correspondent pas.</div>
          )}
        </div>

        {error && <div className="fpc-modal__error">{error}</div>}

        <div className="fpc-modal__actions">
          <button
            type="submit"
            className="fpc-modal__btn"
            disabled={!canSubmit}
          >
            <Lock size={14} strokeWidth={2} />
            {submitting ? 'Mise à jour…' : 'Mettre à jour mon mot de passe'}
          </button>
        </div>

        <div className="fpc-modal__footer-note">
          Les autres sessions actives seront déconnectées automatiquement.
        </div>
      </form>
    </div>
  );
}
